import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Building2, ChevronRight } from "lucide-react";
import { workspaceKindLabel, type WorkspaceKind } from "@/lib/workspaces";

interface SelectableWorkspace {
    kind: WorkspaceKind;
    id: string;
    name: string;
}

interface ClubSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    workspaces: SelectableWorkspace[];
    onSelect: (kind: WorkspaceKind, id: string) => void;
}

/**
 * Shown after login when a person has several workspaces (tenants, and later
 * venues and kiosks) and none is remembered yet.
 */
export function ClubSelectionModal({ isOpen, onClose, workspaces, onSelect }: ClubSelectionModalProps) {
    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Select your workspace</DialogTitle>
                    <DialogDescription>
                        You have access to several workspaces. Please select which one you want to manage.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
                    {workspaces.map((w) => (
                        <Button
                            key={`${w.kind}:${w.id}`}
                            variant="outline"
                            className="w-full justify-between h-auto py-4 px-6 text-left group hover:border-primary transition-all"
                            onClick={() => onSelect(w.kind, w.id)}
                        >
                            <div className="flex items-center gap-4">
                                <div className="bg-primary/10 p-2 rounded-lg group-hover:bg-primary/20 transition-colors">
                                    <Building2 className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="font-semibold text-base">{w.name}</p>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{workspaceKindLabel(w.kind)}</p>
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </Button>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
